import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from '../../components/common/Button';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import { SiteSurveyPublicClient, SubmitSiteSurveyRequestDTO } from '../../services/apiClient';

const apiClient = new SiteSurveyPublicClient(API_BASE_URL, axiosInstance);

type FormState = {
  phone: string;
  usesEarthranger: string;
  erSurvivesOutage: string;
  erAdminContact: string;
  alertTriggers: string[];
  alertTriggersOther: string;
  tracksStaffPositions: string;
  staffTrackingMethod: string;
  droneOwnership: string;
  droneModelDji: string[];
  droneModelOther: string;
  droneModelOtherUnsure: boolean;
  launchMethod: string;
  caaApprovals: string[];
  nightResponder: string[];
  nightResponseTime: string;
  firstSiteInterest: string;
};

const initialState: FormState = {
  phone: '',
  usesEarthranger: '',
  erSurvivesOutage: '',
  erAdminContact: '',
  alertTriggers: [],
  alertTriggersOther: '',
  tracksStaffPositions: '',
  staffTrackingMethod: '',
  droneOwnership: '',
  droneModelDji: [],
  droneModelOther: '',
  droneModelOtherUnsure: false,
  launchMethod: '',
  caaApprovals: [],
  nightResponder: [],
  nightResponseTime: '',
  firstSiteInterest: '',
};

// Loose check for SA formats (with or without country code). Used only to show a
// non-blocking warning — the server normalises to E.164 and never rejects on format.
function looksLikeSaPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return /^0\d{9}$/.test(digits) || /^27\d{9}$/.test(digits);
}

function toggleExclusive(current: string[], clicked: string, exclusiveValues: string[]): string[] {
  if (exclusiveValues.includes(clicked)) {
    return current.includes(clicked) ? [] : [clicked];
  }
  const withoutExclusives = current.filter((v) => !exclusiveValues.includes(v));
  return withoutExclusives.includes(clicked)
    ? withoutExclusives.filter((v) => v !== clicked)
    : [...withoutExclusives, clicked];
}

const Field: React.FC<{ label: string; helper?: string; children: React.ReactNode }> = ({
  label,
  helper,
  children,
}) => (
  <div className="mb-8">
    <label className="block text-base font-semibold text-cream mb-2">{label}</label>
    {helper && <p className="text-sm text-harbor mb-2">{helper}</p>}
    {children}
  </div>
);

const RadioOptions: React.FC<{
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}> = ({ name, value, onChange, options }) => (
  <div className="space-y-2">
    {options.map((opt) => (
      <label
        key={opt.value}
        className={`flex items-center gap-3 border rounded px-4 py-3 cursor-pointer transition-colors ${value === opt.value ? 'border-signal bg-signal/10' : 'border-seam bg-graphite'
          }`}
      >
        <input
          type="radio"
          name={name}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          className="w-5 h-5 accent-signal shrink-0"
        />
        <span className="text-base text-cream">{opt.label}</span>
      </label>
    ))}
  </div>
);

const CheckboxOptions: React.FC<{
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  exclusive?: string[];
}> = ({ values, onChange, options, exclusive = [] }) => (
  <div className="space-y-2">
    {options.map((opt) => (
      <label
        key={opt.value}
        className={`flex items-center gap-3 border rounded px-4 py-3 cursor-pointer transition-colors ${values.includes(opt.value) ? 'border-signal bg-signal/10' : 'border-seam bg-graphite'
          }`}
      >
        <input
          type="checkbox"
          checked={values.includes(opt.value)}
          onChange={() => onChange(toggleExclusive(values, opt.value, exclusive))}
          className="w-5 h-5 accent-signal shrink-0"
        />
        <span className="text-base text-cream">{opt.label}</span>
      </label>
    ))}
  </div>
);

const inputClasses =
  'w-full border border-seam bg-graphite text-cream placeholder:text-harbor/60 rounded px-4 py-3 text-base focus:outline-none focus:border-signal';

const SiteSurvey: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [tokenState, setTokenState] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [label, setLabel] = useState<string>('');
  const [form, setForm] = useState<FormState>(initialState);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }
    apiClient
      .validateSurveyToken(token)
      .then((res) => {
        setLabel(res?.data?.label || '');
        setTokenState('valid');
      })
      .catch(() => setTokenState('invalid'));
  }, [token]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const payload = {
        token,
        phone: form.phone,
        usesEarthranger: form.usesEarthranger,
        erSurvivesOutage: form.usesEarthranger === 'yes' ? form.erSurvivesOutage : undefined,
        erAdminContact: form.usesEarthranger === 'yes' ? form.erAdminContact || undefined : undefined,
        alertTriggers: form.alertTriggers,
        alertTriggersOther: form.alertTriggers.includes('other') ? form.alertTriggersOther || undefined : undefined,
        tracksStaffPositions: form.tracksStaffPositions,
        staffTrackingMethod:
          form.tracksStaffPositions === 'all_staff' || form.tracksStaffPositions === 'some_staff'
            ? form.staffTrackingMethod || undefined
            : undefined,
        droneOwnership: form.droneOwnership,
        droneModelDji: form.droneOwnership === 'dji' ? form.droneModelDji : [],
        droneModelOther:
          form.droneOwnership === 'other'
            ? form.droneModelOtherUnsure
              ? 'unsure'
              : form.droneModelOther || undefined
            : undefined,
        launchMethod: form.droneOwnership !== 'none' ? form.launchMethod : undefined,
        caaApprovals: form.caaApprovals,
        nightResponder: form.nightResponder,
        nightResponseTime: form.nightResponseTime,
        firstSiteInterest: form.firstSiteInterest,
      } as SubmitSiteSurveyRequestDTO;

      await apiClient.submitSiteSurvey(payload);
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
        'We could not submit your response. Please check the form and try again.',
      );
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (tokenState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-charcoal px-4">
        <p className="text-harbor">Loading...</p>
      </div>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-charcoal px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-black uppercase text-cream mb-2">Link not valid</h1>
          <p className="text-harbor">
            This survey link could not be found. Please check the link or contact us for a new one.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-charcoal px-4 py-12">
        <div className="max-w-md text-center flex-1 flex flex-col items-center justify-center">
          <h1 className="text-xl font-black uppercase text-cream mb-2">Thank you</h1>
          <p className="text-harbor">
            Your response has been recorded. We appreciate you taking the time to complete this.
          </p>
          <a href="https://latreiatechnologies.com/" target="_blank" rel="noopener noreferrer" className="mb-6">
            <img src="/images/logo-dark.svg" alt="Latreia" className="h-8 mt-5 w-auto mx-auto" />
          </a>
        </div>
        <footer className="mt-12 text-sm text-harbor">
          To learn more visit{' '}
          <a
            href="https://latreiatechnologies.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cream uppercase font-semibold hover:text-signal"
          >
            Latreia Technologies
          </a>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal py-8 px-4">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-black uppercase text-cream mb-1">Site survey</h1>
        <p className="text-harbor mb-6">
          {label ? `${label} — ` : ''}Ten quick questions, most a single tap. Takes under three minutes.
        </p>

        <div className="border border-seam bg-graphite rounded px-5 py-4 mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-cream mb-2">
            About this survey
          </h2>
          <p className="text-sm text-harbor mb-3">
            We are Latreia Technologies. We are building a system that launches a drone
            automatically when something triggers an alert on your property, and gets what it
            sees to whoever has to respond.
          </p>
          <p className="text-sm text-harbor mb-3">
            These questions tell us what is already running on your property, so we know what
            would actually be involved. About three minutes, most of it one tap. Not sure is a
            real answer.
          </p>
          <p className="text-sm text-harbor">
            Leave a number and we will call you. Answering this does not commit you to anything.
          </p>
        </div>

        {errorMessage && (
          <div className="mb-6 border border-signal bg-signal/10 text-cream text-sm rounded px-4 py-3">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Contact block */}
          <Field label="Phone">
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              className={inputClasses}
            />
            {form.phone.trim() !== '' && !looksLikeSaPhone(form.phone) && (
              <p className="text-sm text-harbor mt-2">
                That doesn't look like a South African number — you can still submit as is.
              </p>
            )}
          </Field>

          <hr className="my-8 border-seam" />

          {/* Q1 */}
          <Field label="1. Do you use EarthRanger?">
            <RadioOptions
              name="usesEarthranger"
              value={form.usesEarthranger}
              onChange={(v) => update('usesEarthranger', v)}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'unsure', label: 'Not sure what that is' },
              ]}
            />
          </Field>

          {/* Q2 */}
          {form.usesEarthranger === 'yes' && (
            <Field label="2. If the farm internet goes down, does EarthRanger still work here on the property?">
              <RadioOptions
                name="erSurvivesOutage"
                value={form.erSurvivesOutage}
                onChange={(v) => update('erSurvivesOutage', v)}
                options={[
                  { value: 'works_offline', label: 'Yes, it keeps working' },
                  { value: 'requires_internet', label: 'No, it goes down too' },
                  { value: 'unknown', label: "Don't know" },
                ]}
              />
            </Field>
          )}

          {/* Q3 */}
          {form.usesEarthranger === 'yes' && (
            <Field
              label="3. Who set EarthRanger up, or who looks after it?"
              helper="A name and a number is enough. If you are not sure, whoever fitted the collars usually knows."
            >
              <input
                type="text"
                value={form.erAdminContact}
                onChange={(e) => update('erAdminContact', e.target.value)}
                className={inputClasses}
              />
            </Field>
          )}

          {/* Q4 */}
          <Field label="4. What sets off an alert that someone has to go and look at?">
            <CheckboxOptions
              values={form.alertTriggers}
              onChange={(v) => update('alertTriggers', v)}
              exclusive={['none']}
              options={[
                { value: 'collars', label: 'GPS collars on the animals' },
                { value: 'camera_traps', label: 'Camera traps' },
                { value: 'fence_alarm', label: 'Fence alarm' },
                { value: 'gate_sensors', label: 'Gate or entrance sensors' },
                { value: 'fixed_thermal', label: 'Fixed thermal or night cameras on poles' },
                { value: 'patrol_radio', label: 'A patrol team calling it in on the radio' },
                { value: 'other', label: 'Something else' },
                { value: 'none', label: 'Nothing automatic, we rely on patrols' },
              ]}
            />
            {form.alertTriggers.includes('other') && (
              <input
                type="text"
                maxLength={120}
                placeholder="What else?"
                value={form.alertTriggersOther}
                onChange={(e) => update('alertTriggersOther', e.target.value)}
                className={`${inputClasses} mt-2`}
              />
            )}
          </Field>

          {/* Q4a */}
          <Field label="4a. Do you track where your rangers or field staff are on the property?">
            <RadioOptions
              name="tracksStaffPositions"
              value={form.tracksStaffPositions}
              onChange={(v) => update('tracksStaffPositions', v)}
              options={[
                { value: 'all_staff', label: 'Yes, all field staff' },
                { value: 'some_staff', label: 'Yes, some of them' },
                { value: 'no', label: 'No' },
                { value: 'unsure', label: 'Not sure' },
              ]}
            />
            {(form.tracksStaffPositions === 'all_staff' || form.tracksStaffPositions === 'some_staff') && (
              <>
                <p className="text-sm text-harbor mt-2 mb-2">
                  Radios, phones, a tracking app, whatever it runs on.
                </p>
                <input
                  type="text"
                  maxLength={120}
                  value={form.staffTrackingMethod}
                  onChange={(e) => update('staffTrackingMethod', e.target.value)}
                  className={inputClasses}
                />
              </>
            )}
          </Field>

          {/* Q5 */}
          <Field label="5. Do you have drones on the property?">
            <RadioOptions
              name="droneOwnership"
              value={form.droneOwnership}
              onChange={(v) => update('droneOwnership', v)}
              options={[
                { value: 'dji', label: 'Yes, DJI' },
                { value: 'other', label: 'Yes, another brand' },
                { value: 'none', label: 'No' },
              ]}
            />
          </Field>

          {/* Q5a */}
          {form.droneOwnership === 'dji' && (
            <Field label="5a. Which DJI model or models?">
              <CheckboxOptions
                values={form.droneModelDji}
                onChange={(v) => update('droneModelDji', v)}
                options={[
                  { value: 'm3t', label: 'Mavic 3 Thermal (M3T)' },
                  { value: 'm3e', label: 'Mavic 3 Enterprise' },
                  { value: 'm30t', label: 'Matrice 30T (M30T)' },
                  { value: 'm300_m350', label: 'Matrice 300 or 350 RTK' },
                  { value: 'consumer_no_thermal', label: 'A Mavic or Phantom without a heat camera' },
                  { value: 'other', label: 'Something else, not on this list' },
                  { value: 'unsure', label: 'Not sure' },
                ]}
              />
            </Field>
          )}

          {/* Q5b */}
          {form.droneOwnership === 'other' && (
            <Field
              label="5b. Which brand, and which model if you know it?"
              helper="The brand on its own is fine if you are not sure of the model."
            >
              <input
                type="text"
                disabled={form.droneModelOtherUnsure}
                value={form.droneModelOther}
                onChange={(e) => update('droneModelOther', e.target.value)}
                className={`${inputClasses} mb-2 disabled:opacity-50`}
              />
              <label className="flex items-center gap-2 text-sm text-harbor cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.droneModelOtherUnsure}
                  onChange={(e) => update('droneModelOtherUnsure', e.target.checked)}
                  className="w-4 h-4 accent-signal"
                />
                Not sure
              </label>
            </Field>
          )}

          {/* Q6 */}
          {(form.droneOwnership === 'dji' || form.droneOwnership === 'other') && (
            <Field label="6. How does the drone get into the air?">
              <RadioOptions
                name="launchMethod"
                value={form.launchMethod}
                onChange={(v) => update('launchMethod', v)}
                options={[
                  { value: 'manual', label: 'Someone carries it out and flies it by hand' },
                  { value: 'dock', label: 'It lives in a weatherproof box outside and takes off on its own' },
                  { value: 'both', label: 'Both' },
                ]}
              />
            </Field>
          )}

          {/* Q7 */}
          <Field label="7. Which CAA approvals do you hold?">
            <CheckboxOptions
              values={form.caaApprovals}
              onChange={(v) => update('caaApprovals', v)}
              exclusive={['none', 'unsure']}
              options={[
                { value: 'roc', label: 'Operating certificate (ROC)' },
                { value: 'night', label: 'Approval to fly at night' },
                { value: 'bvlos', label: 'Approval to fly beyond visual line of sight' },
                { value: 'rpl', label: 'Licensed pilot on the team (RPL)' },
                { value: 'registered', label: 'Drones registered with CAA' },
                { value: 'none', label: 'None of these' },
                { value: 'unsure', label: 'Not sure' },
              ]}
            />
          </Field>

          {/* Q8 */}
          <Field label="8. If an alert comes in at 2am and something is found, who goes out?">
            <CheckboxOptions
              values={form.nightResponder}
              onChange={(v) => update('nightResponder', v)}
              exclusive={['daylight_only']}
              options={[
                { value: 'staff_onsite', label: 'Staff living on the property' },
                { value: 'staff_offsite', label: 'Staff living off site' },
                { value: 'contracted', label: 'Contracted security' },
                { value: 'apu', label: 'Anti poaching unit' },
                { value: 'daylight_only', label: 'Nobody goes out until it is light' },
              ]}
            />
          </Field>

          {/* Q9 */}
          <Field label="9. How long would it take them to reach the far end of the property at night?">
            <RadioOptions
              name="nightResponseTime"
              value={form.nightResponseTime}
              onChange={(v) => update('nightResponseTime', v)}
              options={[
                { value: 'under_15', label: 'Under 15 minutes' },
                { value: '15_30', label: '15 to 30 minutes' },
                { value: '30_60', label: '30 to 60 minutes' },
                { value: 'over_60', label: 'Over an hour' },
                { value: 'daylight_only', label: 'We would not go out until morning' },
              ]}
            />
          </Field>

          {/* Q10 */}
          <Field label="10. Would you be open to being one of the first sites we work with?">
            <RadioOptions
              name="firstSiteInterest"
              value={form.firstSiteInterest}
              onChange={(v) => update('firstSiteInterest', v)}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'maybe', label: 'Maybe, I would want to hear more first' },
                { value: 'no', label: 'Not right now' },
              ]}
            />
          </Field>

          <Button type="submit" className="w-full mt-4" disabled={isSubmitting} isLoading={isSubmitting}>
            Submit
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SiteSurvey;
